// src/pages/Admin/AdminOrders.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../../api/orders.js';
import { useToast } from '../../hooks/useToast.js';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { formatDate, formatPrice } from '../../utils/formatters.js';
import { ORDER_STATUS_LABELS } from '../../utils/constants.js';

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    limit: 50,
  });
  const [total, setTotal] = useState(0);
  const { success, error } = useToast();

  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await ordersAPI.getAll(filters);
      setOrders(response.data.data || []);
      setTotal(response.data.count || 0);
    } catch (err) {
      error('Не удалось загрузить заказы');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, { status: newStatus });
      success('Статус заказа обновлен');
      loadOrders();
    } catch (err) {
      error('Не удалось обновить статус');
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка заказов..." />;
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <input
          type="text"
          placeholder="Поиск по номеру или email..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid rgb(var(--c-border))',
            fontSize: '14px',
            outline: 'none',
            width: '280px',
          }}
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid rgb(var(--c-border))',
            fontSize: '14px',
            outline: 'none',
          }}
        >
          <option value="">Все статусы</option>
          <option value="pending">Ожидает</option>
          <option value="processing">В обработке</option>
          <option value="shipped">Отправлен</option>
          <option value="delivered">Доставлен</option>
          <option value="completed">Завершен</option>
          <option value="cancelled">Отменен</option>
        </select>
        <span style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
          Всего: {total} заказов
        </span>
      </div>

      <div style={{
        backgroundColor: 'rgb(var(--c-surface))',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgb(var(--c-bg))' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>№ заказа</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>Клиент</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>Сумма</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>Статус</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>Дата</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'rgb(var(--c-text-secondary))' }}>
                    Заказы не найдены
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} style={{ borderTop: '1px solid rgb(var(--c-surface-2))' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                      <Link to={`/admin/orders/${order.id}`} style={{ color: 'rgb(var(--c-accent))', textDecoration: 'none' }}>
                        {order.order_number}
                      </Link>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {order.User?.first_name || order.guest_email || 'Гость'}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                      {formatPrice(order.total)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: '1px solid rgb(var(--c-border))',
                          fontSize: '12px',
                          outline: 'none',
                          backgroundColor: 'rgb(var(--c-surface))',
                        }}
                      >
                        {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '12px 16px', color: 'rgb(var(--c-text-secondary))' }}>
                      {formatDate(order.createdAt)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <Link to={`/admin/orders/${order.id}`}>
                        <button
                          style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgb(var(--c-accent))',
                            backgroundColor: 'transparent',
                            color: 'rgb(var(--c-accent))',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Подробнее
                        </button>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};